import './RestaurantsCard.css';
import React, { memo } from 'react';
import ScrollAnimation from 'react-animate-on-scroll';
import { canUseDOM, get, getListingItemURL, getMediaHost, injectImageResolution, pathJoin } from '../../../utils';
import { isNil, isNotNil } from '../../../utils/IsNil';
import RouterLink from '../RouterLink';
import LazyPicture from '../LazyPicture';
import TripAdvisor from '../TripAdvisor/TripAdvisor';
import FavouriteBtn from '../FavouriteBtn/FavouriteBtn';
import { ReactComponent as MichelinGreenStar } from '../../../resources/icons/michelinGreenStar.svg';
import { ReactComponent as MichelinRedStar } from '../../../resources/icons/michelinRedStar.svg';
import { HighlighterHOC } from '../HighlighterHOC/HighlighterHOC';
import { tealiumClickFunc, tealiumMergeClickData } from '../../../utils/tealium';
const allAvailableMatchesOfCountRedStars = {
  "one": 1,
  "1": 1,
  "two": 2,
  "2": 2,
  "three": 3,
  "3": 3
};
const defaultEmptyAnalyticObject = {
  bannerClickAnalytics: null,
  titleClickAnalytics: null,
  addToFavoriteClickAnalytics: null,
  removeFromFavoriteClickAnalytics: null
};
const RestaurantsCard = /*#__PURE__*/memo(_ref => {
  let {
    item,
    reviews,
    rtl,
    analyticsCallback,
    textToHighlight,
    baseHrefLink,
    isGetFullUrl = false,
    params
  } = _ref;
  const analyticObject = (() => {
    if (isNil(analyticsCallback)) {
      return defaultEmptyAnalyticObject;
    }
    return analyticsCallback;
  })();
  const hrefLink = canUseDOM ? baseHrefLink ? baseHrefLink : window.location.href : "";
  const tripAdvRatingImage = get(item, 'tripadvisorImage');
  const mainTripadvisorPrice = get(item, 'tripadvisorPrice');
  const mainTripadvisorReviews = get(item, 'tripadvisorReviews');
  const tripAdvScore = get(item, 'tripadvisorScore');
  const itemCategory = get(item, 'category');
  const michelinStarName = get(item, 'michelinStarName'); // Always has ENGLISH translation -> DON`T show this
  const michelinStarTitle = get(item, 'michelinStarTitle'); // Has MULTIPLE translation -> show only this

  const imgSrc = injectImageResolution(`${pathJoin([getMediaHost(), item.image])}`, 340, 243);
  const getHref = () => isGetFullUrl ? item.url : getListingItemURL(hrefLink, item.urlSlug, item.url);
  const DefineCountOfMichelinStars = () => {
    let textVersionOfRedStarsCount = null;

    // check for green star
    if (michelinStarName[0].toLowerCase().includes("green")) {
      return /*#__PURE__*/React.createElement(MichelinGreenStar, {
        className: "michelinStar"
      });
    }

    // check for count of red stars
    Object.entries(allAvailableMatchesOfCountRedStars).some(_ref2 => {
      let [textVersionOfRedStars] = _ref2;
      if (michelinStarName[0].toLowerCase().includes(textVersionOfRedStars)) {
        textVersionOfRedStarsCount = textVersionOfRedStars;
        return true;
      }
    });
    if (isNotNil(textVersionOfRedStarsCount)) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, new Array(allAvailableMatchesOfCountRedStars[textVersionOfRedStarsCount]).fill(null).map(() => /*#__PURE__*/React.createElement(MichelinRedStar, {
        className: "michelinStar"
      })));
    }
    return null;
  };
  return /*#__PURE__*/React.createElement("div", {
    className: `${commonStyles} ${rtl ? rtlStyles : ltrStyles}`,
    style: {
      width: "100%"
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "item"
  }, /*#__PURE__*/React.createElement("div", {
    className: "image-wrap"
  }, item.image ? /*#__PURE__*/React.createElement(RouterLink, {
    openInSameTab: true,
    className: "link__a",
    title: "poi link",
    "aria-label": "link",
    href: getHref(),
    onClick: () => {
      if (analyticObject.bannerClickAnalytics) {
        tealiumClickFunc(tealiumMergeClickData(analyticObject.bannerClickAnalytics, params), item.title);
      }
    }
  }, /*#__PURE__*/React.createElement(LazyPicture, {
    useEmptyPlaceholder: true,
    hover: true
  }, /*#__PURE__*/React.createElement("source", {
    media: "(min-width: 768px)",
    srcSet: imgSrc
  }), /*#__PURE__*/React.createElement("img", {
    className: "image",
    src: imgSrc,
    alt: item.title
  }))) : null), /*#__PURE__*/React.createElement("div", {
    className: "info"
  }, /*#__PURE__*/React.createElement(ScrollAnimation, {
    animateIn: "animate__fadeInUp",
    animateOnce: true,
    duration: 1,
    className: "info_scrollAnimation"
  }, item && michelinStarTitle && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(DefineCountOfMichelinStars, null), /*#__PURE__*/React.createElement("span", {
    className: "text-michelinStar"
  }, michelinStarTitle)), item && isNil(michelinStarTitle) && itemCategory && /*#__PURE__*/React.createElement("div", {
    className: "text-cuisine"
  }, /*#__PURE__*/React.createElement(HighlighterHOC, {
    text: itemCategory.join(', '),
    searchString: textToHighlight
  })), item && /*#__PURE__*/React.createElement(RouterLink, {
    openInSameTab: true,
    className: "item-title",
    href: getHref(),
    onClick: () => {
      if (analyticObject.titleClickAnalytics) {
        tealiumClickFunc(tealiumMergeClickData(analyticObject.titleClickAnalytics, params), item.title);
      }
    }
  }, item ? /*#__PURE__*/React.createElement(HighlighterHOC, {
    text: item.title,
    searchString: textToHighlight
  }) : null), /*#__PURE__*/React.createElement("p", {
    className: "body-small text"
  }, item ? /*#__PURE__*/React.createElement(HighlighterHOC, {
    text: item.info || item.introText,
    searchString: textToHighlight
  }) : null), /*#__PURE__*/React.createElement("div", {
    className: "tripAdvisorContainer"
  }, /*#__PURE__*/React.createElement(TripAdvisor, {
    imageRatingSRC: tripAdvRatingImage,
    score: tripAdvScore,
    alt: "Tripadvisor",
    tripadvisorReviews: mainTripadvisorReviews,
    tripadvisorPrice: mainTripadvisorPrice,
    reviewCaption: reviews
  }))), /*#__PURE__*/React.createElement(FavouriteBtn, {
    itemId: item && item.objectID,
    checkFavouriteButton: item && item.objectID,
    classes: "fav-btn",
    analyticsClick: (buttonTitle, active) => {
      if (active) {
        analyticObject.removeFromFavoriteClickAnalytics && tealiumClickFunc(tealiumMergeClickData(analyticObject.removeFromFavoriteClickAnalytics, params), item.title);
      } else {
        analyticObject.addToFavoriteClickAnalytics && tealiumClickFunc(tealiumMergeClickData(analyticObject.addToFavoriteClickAnalytics, params), item.title);
      }
    }
  }))));
});
const commonStyles = "c7h740z";
const ltrStyles = "luf3mwf";
const rtlStyles = "r15ns9pm";
export { RestaurantsCard };